import { Box, Chip, IconButton, Skeleton, Tab, Tabs, Typography, TypographyOwnProps, useTheme } from '@mui/material'
import { useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import { useAppSelector } from '../../store/Hooks'
import RouteHeader from '../controls/RouteHeader'
import SmartCard from '../controls/cards/SmartCard'
import KPICard from '../controls/data_visualization/KPICard'
import StaleObserver from '../controls/stale/StaleObserver'
import SmartTable from '../controls/table/SmartTable'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import RoutingManager from '../routing/Routing'
import Helper from '../../helpers/Helper'
import TimeHelper from '../../helpers/TimeHelper'
import { useDispatch } from 'react-redux'
import { changeRoute } from '../../store/slices/SessionSlice'
import ClaimLinesTable from '../controls/ClaimLinesTable'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { SmartTableUtilities } from '../controls/table'

export default function MemberDetails() {
    const theme = useTheme()
    const dispatch = useDispatch()
    const params = RoutingManager.getParams<{ memberId: number }>(useAppSelector(s => s.session.routeParams))
    const time = useAppSelector(s => s.app.timeSettings ?? TimeHelper.timeSettings())
    const [value, setValue] = useState(0)
    const [permission, setPermission] = useState(false)

    const { status, response } = useQuery({
        queryKey: buildQueryKey('memberDetails', params.memberId, permission?.toString()),
        queryFn: () => permission ? ApiClient.getMemberDetailsWithPermission(params.memberId, time) : ApiClient.getMemberDetails(params.memberId, time),
        cache: permission ? false : true
    })
    const isLoading = status === 'loading'
    const havePermisison = permission

    const renderUserData = (text: string, skeletonWidth?: number, variant?: TypographyOwnProps['variant'], forceShow?: boolean) => {
        const needBlur = isLoading || forceShow || havePermisison

        return (
            <Box display='flex' flexDirection='row' sx={{ WebkitFilter: needBlur ? '' : 'blur(5px)', userSelect: needBlur ? '' : 'none' }}>
                {isLoading
                    ? <Skeleton variant='text' sx={{ fontSize: (variant ?? 'subtitle1') + '.fontSize', width: skeletonWidth + '%', marginLeft: 0.5 }} />
                    : <Typography variant={variant ?? 'subtitle1'}>{text}</Typography>}
            </Box>
        )
    }

    const name = havePermisison ? [response?.member?.firstName, response?.member?.middleName, response?.member?.lastName].filter(x => x !== 'null' && x != 'undfined').join(' ') : 'Loremsit ipsum'
    const externalId = response?.member?.externalId
    const sex = response?.member?.sex
    const age = response?.member?.age
    const ssn = havePermisison ? response?.member?.SSN : 'Lorem ipsum dolor sit'
    const address = havePermisison ? response?.member?.address : 'Lorem dolor sit'
    const city = havePermisison ? response?.member?.city : 'Lorem ipsum dolor'
    const postalCode = havePermisison ? response?.member?.postalCode : 'Lorem ipsum '
    const email = havePermisison ? response?.member?.email : 'Lorem.ipsum@dolor.sit'

    return (
        <Tile.Container style={{ height: '100%' }}>
            <RouteHeader primary='Member Details' />
            <Tile.Column spacing={2} style={{ height: '100%', overflowY: 'auto' }}>
                <Tile.Row flex={0} spacing={2}>
                    <SmartCard flex={1}>
                        <KPICard
                            data={Helper.dataframeToKPICard(response?.kpi?.totalClaims)}
                            isLoading={isLoading}
                            unit='#'
                            inverse
                            label='Total Claims'
                        />
                    </SmartCard>
                    <SmartCard flex={1}>
                        <KPICard
                            data={Helper.dataframeToKPICard(response?.kpi?.expenses)}
                            isLoading={isLoading}
                            unit='$'
                            inverse
                            label='Total Claims'
                        />
                    </SmartCard>
                </Tile.Row>
                <Tile.Row spacing={2} flex={1} style={{ overflowY: 'auto' }}>
                    <SmartCard flex={1} style={{ overflowY: 'auto', height: '100%', position: 'relative' }}>
                        {!havePermisison && (
                            <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'row', paddingBottom: 3 }}>
                                <IconButton onClick={() => setPermission(!permission)} sx={{ marginRight: 1 }}>
                                    <VisibilityOffIcon fontSize='small' color='disabled' />
                                </IconButton>
                                <Typography fontSize={12} color='GrayText'>
                                    Insufficient permissions to view sensitive content.
                                </Typography>
                            </Box>
                        )}
                        <Box justifyContent='center' alignItems='center' display='flex' sx={{ WebkitFilter: isLoading || havePermisison ? '' : 'blur(8px)' }}>
                            <Box sx={{ width: '50%', aspectRatio: 1, borderRadius: '100%', backgroundColor: theme.palette.divider, justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                                {!isLoading && (
                                    <img
                                        src={response?.member?.image}
                                        style={{ width: '100%', aspectRatio: 1, objectFit: 'cover', borderRadius: '100%', backgroundColor: theme.palette.divider }}
                                    />
                                )}
                            </Box>
                        </Box>
                        <Box position='sticky' top={-15} left={0} marginLeft={-1} paddingLeft={1} marginTop={2} style={{ backgroundColor: theme.palette.mode === 'dark' ? '#232323' : '#ffffff', zIndex: 2 }}>
                            <Box paddingBottom={1}>
                                {renderUserData(name, 80, 'h5')}
                                {renderUserData(externalId, 30, 'subtitle1', true)}
                                {renderUserData(sex + ' - ' + age + ' years old', 50, 'subtitle1', true)}
                            </Box>
                        </Box>
                        <Tile.Column>
                            {renderUserData('SSN: ' + ssn, 50, 'body1')}
                            <Box flex={1} />
                            {renderUserData(address, 40, 'subtitle2')}
                            {renderUserData(city + ' - ' + postalCode, 50, 'subtitle2')}
                            {renderUserData(email, 55, 'body1')}
                        </Tile.Column>
                    </SmartCard>
                    <SmartCard flex={2}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={value} onChange={(_, newValue) => setValue(newValue)} aria-label='basic tabs example'>
                                <Tab label='Contracts' />
                                <Tab label='Providers' />
                                <Tab label='Claims' />
                            </Tabs>
                        </Box>
                        {value === 0 && (
                            <Box sx={{ overflowY: 'auto', height: '100%', position: 'relative' }}>
                                <SmartTable
                                    columns={[
                                        { id: 'name', label: 'Name' },
                                        { id: 'counterparty.name', label: 'Entity' },
                                        { id: 'startDate', label: 'Start date' },
                                        { id: 'status', label: 'Status' },
                                        {
                                            id: 'type', label: 'Type', customRenderer: x => {
                                                return <Chip style={{ fontWeight: 600 }} label={x?.value?.toString()} />
                                            }
                                        },
                                        { id: 'riskscore', label: 'Risk Score' },
                                        { id: 'lineofbusiness', label: 'Line of Business' }
                                    ]}
                                    isLoading={status === 'loading'}
                                    noCollapse
                                    onClickRow={i => dispatch(changeRoute({ route: 'contract-details', params: { contract: response?.contracts[i] }, customRouteName: response?.contracts[i]?.name }))}
                                    datasource={{
                                        rows: (response?.contracts ?? [])?.map((x, i) => SmartTableUtilities.toRow([
                                            { value: x?.name },
                                            { value: x?.counterparty?.name },
                                            { value: new Date(x.startDate).toLocaleDateString() },
                                            { value: x.status?.name },
                                            { value: x.type?.name },
                                            { value: response?.riskScores?.find(k => k.contractId === x.id)?.riskScore },
                                            { value: x.lineOfBusinesses?.map(x => x.name).join(', ') }
                                        ], i))
                                    }}
                                />
                            </Box>
                        )}
                        {value === 1 && (
                            <Box sx={{ overflowY: 'auto', height: '100%', position: 'relative' }}>
                                <SmartTable
                                    columns={[
                                        { id: 'name', label: 'Provaider Name' },
                                        { id: 'counterparty.name', label: 'Entity' }
                                    ]}
                                    isLoading={status === 'loading'}
                                    noCollapse
                                    onClickRow={i => {
                                        dispatch(changeRoute({
                                            route: 'provider-contracts',
                                            params: { providerId: response?.providers[i].id },
                                            customRouteName: response?.providers[i].name
                                        }))
                                    }}
                                    datasource={{
                                        rows: (response?.providers ?? [])?.map((x, i) => SmartTableUtilities.toRow([
                                            { value: x?.name },
                                            { value: x?.counterparty?.name }
                                        ], i))
                                    }}
                                />
                            </Box>
                        )}
                        {value === 2 && (
                            <Box sx={{ overflowY: 'auto', height: '100%', position: 'relative' }}>
                                <SmartTable
                                    isLoading={isLoading}
                                    sticky
                                    columns={[
                                        { id: 'action', label: '' },
                                        { id: 'datetime', label: 'Datetime' },
                                        { id: 'provider', label: 'Provider' },
                                        { id: 'status', label: 'Status' },
                                        { id: 'isBadClaim', label: 'Is Bad Claim' },
                                        { id: 'totalclaimlines', label: 'N° Claimlines', numeric: true },
                                        { id: 'totalpayment', label: 'TotalPayment', numeric: true }
                                    ]}
                                    datasource={{
                                        rows: (response?.claims ?? []).map((x, i) =>
                                            SmartTableUtilities.toRow(
                                                [
                                                    { value: x?.datetime },
                                                    { value: x?.provider?.name },
                                                    { value: x?.status },
                                                    { value: x?.isBadClaim?.toString() },
                                                    { value: x?.totalclaimlines },
                                                    { value: x?.totalPayment }
                                                ],
                                                i,
                                                <ClaimLinesTable key={x.id} claim={x} hideMemberId />
                                            )
                                        )
                                    }}
                                />
                            </Box>
                        )}
                    </SmartCard>
                </Tile.Row>
            </Tile.Column>
            <StaleObserver status={status} />
        </Tile.Container>
    )
}
